import type { ButtonHTMLAttributes } from 'react';

import { cn } from '@/lib/cn';

/**
 * L'INTERRUTTORE (`role="switch"`) — uno solo, per ogni schermata che ne ha uno (I-334, Luca 2026-10-04 ~11:5xZ:
 * «Negli screenshot vedi i selettori che non si vedono correttamente (sono in sezione qsocials), fixa»).
 *
 * 🔴 **Il pomello e' ANCORATO a sinistra (`left-1`) e si sposta solo col `translate`.** Fino a I-334 c'erano due copie
 * inline (Amici → privacy e Amici → Notifiche) col pomello `absolute` SENZA `left`: dentro un `<button>` la posizione
 * statica e' il CENTRO (il bottone centra il contenuto), quindi il pomello partiva da 24 px — spento stava a destra,
 * acceso usciva di 20 px dal binario (misura del 04/10, 360 e 390). ⇒ Il pomello qui ha sempre un ancoraggio
 * esplicito, e `tests/interruttore.test.tsx` lo pretende.
 *
 * Geometria: binario 48×28 (`w-12 h-7`), pomello 20×20 (`h-5 w-5`) a 4 px dal bordo; spento a sinistra
 * (`translate-x-0`), acceso a destra (`translate-x-5`, cioe' 48 − 20 − 4 − 4 = 20 px): 4 px di margine da ogni lato.
 * Colori: acceso = binario `bg-brand`; spento = binario neutro col CONTORNO `fg-muted`, perche' il fondo `bg-tertiary`
 * da solo sulla card misurava 1,0–1,2:1 (il binario non si vedeva: «Promozioni», solo il pomello).
 * Non disponibile (`disabled` o `aria-disabled`) = tutto l'interruttore a meta' opacita' e il cursore «vietato»: si legge
 * spento-o-acceso E non premibile; il perche' lo dice la riga (es. il motivo di una riga `locked`).
 *
 * ⚠️ `aria-disabled` e `disabled` restano di chi lo monta: una riga `locked` usa `aria-disabled` per restare
 * raggiungibile col Tab, un salvataggio in volo usa `disabled`. Il componente li disegna uguali.
 */
export interface InterruttoreProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type' | 'role' | 'aria-checked' | 'children' | 'className'> {
  acceso: boolean;
}

export function Interruttore({ acceso, ...resto }: InterruttoreProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={acceso}
      {...resto}
      className={cn(
        'relative h-7 w-12 shrink-0 rounded-full transition-colors',
        // 🔑 TOCCO ≥ 44 (I-334, tma-orcb 04/10 15:11Z): il disegno resta 48×28 col pomello a 4 px dal bordo; l'AREA del
        // tocco e' 48×44 con un `::before` trasparente 8 px sopra e 8 sotto. Vale per ogni interruttore dell'app.
        "before:absolute before:inset-x-0 before:-inset-y-2 before:content-['']",
        'disabled:cursor-not-allowed disabled:opacity-50 aria-disabled:cursor-not-allowed aria-disabled:opacity-50',
        acceso ? 'bg-brand' : 'bg-bg-tertiary ring-2 ring-inset ring-fg-muted',
      )}
    >
      <span
        aria-hidden
        data-pomello
        className={cn(
          'absolute left-1 top-1 h-5 w-5 rounded-full bg-white shadow-sm transition-transform',
          acceso ? 'translate-x-5' : 'translate-x-0',
        )}
      />
    </button>
  );
}
